import { DateTime } from 'luxon';
import { groupBy } from 'lodash';
import { InteractionType } from '@lendi/core-constants';
import { Brand } from '@lendi/lala-react';
import { getIntervalAvailabilities, roundToIntervalAfter, roundToIntervalBefore } from '@lendi/availability-intervals';
import { getHalfday, getHourWith12HourClock, getMinutesWithFormat } from '../format-datetime';
import { getAppointmentLength } from '../appointment';

export const DEFAULT_BROKER_TIMEZONE = 'Australia/Sydney';

export type Halfday = 'AM' | 'PM';
const WORKING_HOURS_START = 8;
const WORKING_HOURS_END = 20;
const INTERVAL_MINUTES = 15;
const DAYS_IN_FUTURE = 28;
const DAYS_IN_FUTURE_SHORT = 7;

// Only used for stub, need to be removed in the future
export const interactionTypeIntervalMapping = {
  [InteractionType.Phone]: 15,
  [InteractionType.FaceToFace]: 30,
  [InteractionType.Video]: undefined,
  [InteractionType.Store]: undefined, //There was no Video, Store before here, so assign undefined to make type checking pass
};

export interface AvailableAppointmentTimeString {
  datetime: string;
  brokerIds: string[];
}

export interface AvailableAppointmentDate {
  datetime: DateTime;
  brokerIds: string[];
}

export interface BaAData {
  AM: AvailableAppointmentDate[];
  PM: AvailableAppointmentDate[];
}

export interface BaADataGroupByDate {
  day: string;
  data: BaAData;
}

export interface TeamTimeslot {
  timeslot: DateTime;
  isAvailable: boolean;
  brokerIds?: string[];
}

export interface TeamTimeslotByDate {
  [date: string]: TeamTimeslot[];
}

export const convertISOStringToDate = (availableDateISOString: AvailableAppointmentTimeString, timezone?: string) => {
  return {
    datetime: DateTime.fromISO(availableDateISOString.datetime, { zone: timezone }),
    brokerIds: availableDateISOString.brokerIds,
  };
};

export const getAllAvailableDate = (
  availableDates: AvailableAppointmentTimeString[],
  timezone: string,
  filterOutFifteenMinuteTimeslots: boolean = false
) => {
  const allAvailableDates: string[] = [];

  availableDates.forEach((availableDate) => {
    const datetimeInstance = DateTime.fromISO(availableDate.datetime, { zone: timezone });
    const isoDate = datetimeInstance.toISODate();

    if (!allAvailableDates.includes(isoDate)) {
      if (filterOutFifteenMinuteTimeslots) {
        if (![15, 45].includes(datetimeInstance.minute)) {
          allAvailableDates.push(isoDate);
        }
      } else {
        allAvailableDates.push(isoDate);
      }
    }
  });

  return allAvailableDates;
};
export const groupAvailableTimes = (
  availableTimeStrings: AvailableAppointmentTimeString[],
  timezone: string,
  filterOutFifteenMinuteTimeslots: boolean = false
): BaADataGroupByDate[] => {
  const availabilityTimes = filterOutFifteenMinuteTimeslots
    ? availableTimeStrings.filter(
        (availableTimeString) =>
          ![15, 45].includes(DateTime.fromISO(availableTimeString.datetime, { zone: timezone }).minute)
      )
    : availableTimeStrings;

  return availabilityTimes.reduce((total, value) => {
    const converted = DateTime.fromISO(value.datetime, { zone: timezone });
    const date = converted.toISODate();

    const newValue = {
      datetime: converted,
      brokerIds: value.brokerIds,
    };

    if (total.length === 0 || (total[total.length - 1] || {}).day !== date) {
      const newobj: BaADataGroupByDate = {
        day: date,
        data: { AM: [], PM: [] },
      };
      converted.hour >= 12 ? newobj.data.PM.push(newValue) : newobj.data.AM.push(newValue);
      total.push(newobj);
    } else {
      const prevObj = total[total.length - 1];
      converted.hour >= 12 ? prevObj.data.PM.push(newValue) : prevObj.data.AM.push(newValue);
    }

    return total;
  }, [] as BaADataGroupByDate[]);
};

export const groupAvailableTimesTeam = (availableTimeStrings: AvailableAppointmentTimeString[], timezone: string) => {
  let groupDataByDate: { [key: string]: TeamTimeslot[] } = {};
  const availableDates: string[] = getAllAvailableDate(availableTimeStrings, timezone, false);

  groupDataByDate = availableDates.reduce((obj, date) => {
    return {
      ...obj,
      [date]: [],
    };
  }, {});

  const dateKeys = Object.keys(groupDataByDate);

  availableTimeStrings.forEach((availableTimeString) => {
    const isoDate = DateTime.fromISO(availableTimeString.datetime).toISODate();
    const availableDate = convertISOStringToDate(availableTimeString, timezone);

    if (dateKeys.includes(isoDate)) {
      groupDataByDate[isoDate].push({
        timeslot: availableDate.datetime,
        isAvailable: true,
        brokerIds: availableTimeString.brokerIds,
      });
    }
  });
  return groupDataByDate;
};

export const getAppointmentTimeRangesLabel = (
  brand: Brand,
  date: DateTime,
  appointmentType: InteractionType
): string => {
  const appointmentLength = getAppointmentLength(brand, appointmentType);

  const startTimeString = `${getHourWith12HourClock(date)}:${getMinutesWithFormat(date)} ${getHalfday(date)}`;
  const endTime = date.plus({ minutes: appointmentLength });
  const endTimeString = `${getHourWith12HourClock(endTime)}:${getMinutesWithFormat(endTime)} ${getHalfday(endTime)}`;
  return `${startTimeString} to ${endTimeString}`;
};

export const getAppointmentStartTimeLabel = (date: DateTime): string =>
  `${getHourWith12HourClock(date)}:${getMinutesWithFormat(date)}${getHalfday(date).toLowerCase()}`;

export const groupAllTimesTeam = (
  availableTimeStrings: AvailableAppointmentTimeString[] | undefined,
  timezone?: string
) => {
  //Ticket AQR-270: The working hours start and end is for TV broker myself journey unavailable time slots
  const WORKING_HOURS_START = 0;
  const WORKING_HOURS_END = 24;
  let groupDataByDate: { [key: string]: TeamTimeslot[] } = {};
  const availabilities = getIntervalAvailabilities(
    WORKING_HOURS_START,
    WORKING_HOURS_END,
    roundToIntervalBefore(DateTime.local(), INTERVAL_MINUTES),
    roundToIntervalAfter(DateTime.local().plus({ days: DAYS_IN_FUTURE }), INTERVAL_MINUTES),
    INTERVAL_MINUTES
  );

  const allTimeslotsWithAvailFlag = availabilities.map((availability) => {
    const isAvailable = availableTimeStrings
      ? availableTimeStrings.some((availableTimeString) => availableTimeString.datetime === availability)
      : false;
    return { timeslot: DateTime.fromISO(availability, { zone: timezone }), isAvailable };
  });

  groupDataByDate = groupBy(
    allTimeslotsWithAvailFlag as any,
    (allTimeslot: TeamTimeslot) => allTimeslot.timeslot.toISO().split('T')[0]
  ) as unknown as { [key: string]: TeamTimeslot[] };
  return groupDataByDate;
};

export const getNearestFutureIntervalStartTime = (interval: number, tz?: string) => {
  const timezone = tz || DEFAULT_BROKER_TIMEZONE;
  const currentTime = DateTime.local().setZone(timezone);

  const roundedMins = Math.ceil(currentTime.minute / interval) * interval;
  return currentTime.set({ minute: roundedMins });
};

export const genStubAvails = (interactionType: InteractionType) => {
  const intervalMins = interactionTypeIntervalMapping[interactionType];

  if (!intervalMins) return [];
  const currentTimeWithGracePeriod = DateTime.local().plus({ hours: 1 });
  const availabilities = getIntervalAvailabilities(
    WORKING_HOURS_START,
    WORKING_HOURS_END,
    roundToIntervalBefore(DateTime.local(), intervalMins),
    roundToIntervalAfter(DateTime.local().plus({ days: DAYS_IN_FUTURE_SHORT }), intervalMins),
    intervalMins
  );

  return availabilities
    .filter((avail) => avail >= currentTimeWithGracePeriod.toUTC().toISO())
    .map((time) => ({
      datetime: time,
      brokerIds: [],
    }));
};
